import React, { Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, Route, Switch } from 'react-router-dom';

// Mandatory CSS required by the Data Grid
// Default Row Model & Module Registry
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry } from '@ag-grid-community/core';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import { Capacitor } from '@capacitor/core';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import {
    IonApp,
    IonFooter,
    IonIcon,
    IonLabel,
    IonLoading,
    IonRouterOutlet,
    IonSpinner,
    IonTabBar,
    IonTabButton,
    IonTabs,
    IonTitle,
    IonToolbar,
    isPlatform,
    setupIonicReact,
    useIonToast,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';
import { init as sentryInit } from '@sentry/capacitor';
import { browserTracingIntegration } from '@sentry/react';
import { compare } from 'compare-versions';
import { calendar, checkbox, closeOutline, cog, layers, time } from 'ionicons/icons';
import { useAtomValue } from 'jotai';

import './Global.scss';
/* Theme variables */
import './theme/variables.css';

import AppUrlListener from './AppUrlListener';
import FallbackComponent from './components/FallbackComponent';
import Toast from './components/Toast';
import { SplitPane } from './components/web/SplitPane';
import { OnlineStatusProvider } from './hooks/useOnlineStatus';
import { useAppConfig } from './hooks/useSwr';
import DetailLeave from './pages/DetailLeave';
import LeaveCalendar from './pages/LeaveCalendar';
import Login from './pages/Login';
import Logout from './pages/Logout';
import ManualPointing from './pages/ManualPointing';
import MyLeave from './pages/MyLeave';
import { MyPointing } from './pages/MyPointing';
import Pointing from './pages/Pointing';
import { PointingConfirm } from './pages/PointingConfirm';
import ResetPassword from './pages/ResetPassword';
import Schedule from './pages/Schedule';
import Settings from './pages/Settings';
import { ChooseLeaveState } from './pages/validation/ChooseLeaveState';
import ValidationLeave from './pages/validation/Leave';
import ValidationPointage from './pages/validation/Pointage';
import ValidationByEmployee from './pages/validation/ValidationByEmployee';
import { defaultToastOptions, loadingAtom, onlineStatusAtom, usersAtom } from './utils/Global';
import { IframeRoute, LoggedRoute, UnLoggedRoute } from './utils/RouteWrapper';

// Register the RowModel Module
ModuleRegistry.registerModules([ClientSideRowModelModule]);

setupIonicReact();

sentryInit({
    dsn: 'https://ed9c3b1ce9408e653b30584d9e97e9b5@o4508087208968192.ingest.de.sentry.io/4508087210475600',
    integrations: [browserTracingIntegration()],
    tracesSampleRate: 1.0,
    enabled: process.env.NODE_ENV !== 'development', // Pour ne pas utiliser sentry en mode dev
});

const Loading: React.FC = () => {
    const loading = useAtomValue(loadingAtom);
    return <IonLoading isOpen={!!loading} message={'Chargement...'} />;
};

export const BottomSlotWarning: React.FC = () => {
    return (
        <>
            <WarningNewUpdate />
        </>
    );
};

export const WarningNewUpdate: React.FC = () => {
    if (Capacitor.getPlatform() === 'web') {
        return null;
    }

    const [isHidden, setIsHidden] = useState(true);

    useEffect(() => {
        async function getAppVersion() {
            try {
                const result = await AppUpdate.getAppUpdateInfo();
                let currentVersion = '';
                let availableVersion = '';

                if (Capacitor.getPlatform() === 'android') {
                    currentVersion = result.currentVersionCode;
                    availableVersion = result.availableVersionCode!;
                } else {
                    currentVersion = result.currentVersionName;
                    availableVersion = result.availableVersionName!;
                }

                if (currentVersion && availableVersion && compare(currentVersion, availableVersion, '<')) {
                    setIsHidden(false);
                }
            } catch (err) {
                console.error(err);
                return null;
            }
        }

        getAppVersion();
    }, []);

    const openAppStore = async () => {
        await AppUpdate.openAppStore();
    };

    const hideWarning = () => {
        setIsHidden(true);
    };

    if (isHidden) {
        return null;
    }

    return (
        <IonFooter>
            <IonToolbar color="warning" className="ion-text-center">
                <button onClick={openAppStore}>
                    <IonTitle>
                        <u>Mise à jour disponible !</u>
                    </IonTitle>
                </button>
                <button onClick={hideWarning} className="h-8 absolute right-4">
                    <IonIcon style={{ fontSize: '30px' }} icon={closeOutline} />
                </button>
            </IonToolbar>
        </IonFooter>
    );
};

const App: React.FC = () => {
    return (
        <IonApp className={'platform-' + getImplicitPlatform()}>
            <Toast />
            <Loading />
            <OnlineStatusProvider />
            <IonReactRouter>
                <AppUrlListener />
                <ErrorBoundary FallbackComponent={FallbackComponent}>
                    <Suspense
                        fallback={
                            <div className="grid h-screen place-items-center">
                                <IonSpinner />
                            </div>
                        }
                    >
                        <SplitPane>
                            <MainRouter />
                        </SplitPane>
                    </Suspense>
                </ErrorBoundary>
            </IonReactRouter>
        </IonApp>
    );
};

export default App;

export const InsideTabRouter: React.FC = () => {
    const { data: appConfig } = useAppConfig();
    const { mainUser } = useAtomValue(usersAtom);

    const isModePointage = appConfig?.appMode === 'live';
    const isModeManual = appConfig?.appMode === 'manual';

    const isAdherent = mainUser?.type === 'adherent';
    const isGeiq = mainUser?.type === 'geiq';
    const isEmployee = mainUser?.type === 'employee';

    return (
        <Switch>
            {isModePointage && isEmployee && <LoggedRoute exact path="/pointage" component={Pointing} />}
            {isModePointage && isAdherent && <LoggedRoute exact path="/saisie" component={ManualPointing} />}
            {isEmployee && <LoggedRoute exact path="/mesconges" component={MyLeave} />}
            {isModePointage && isEmployee && <LoggedRoute exact path="/mespointage" component={MyPointing} />}
            {isModeManual && <LoggedRoute exact path="/saisie" component={ManualPointing} />}
            {isAdherent && <LoggedRoute exact path="/validation" component={ValidationPointage} />}
            {isAdherent && <LoggedRoute exact path="/choose-leaves" component={ChooseLeaveState} />}
            {isAdherent && <LoggedRoute exact path="/validationconges" component={ValidationLeave} />}

            {/* not in V1 */}
            {/* {isModePointage && (isEmployee || isAdherent) && (
                <LoggedRoute exact path="/calendar" component={Calendar} />
            )} */}
            {isGeiq && <LoggedRoute exact path="/schedule" component={Schedule} />}
            <LoggedRoute exact path="/settings" component={Settings} />
            <LoggedRoute exact path="/reset" component={ResetPassword} />
            <Route render={() => <Redirect to="/login" />} />
        </Switch>
    );
};

const MainRouter: React.FC = () => {
    const { data: appConfig } = useAppConfig();
    const { mainUser } = useAtomValue(usersAtom);
    const isOnline = useAtomValue(onlineStatusAtom);
    const [presentToast] = useIonToast();

    useEffect(() => {
        if (isOnline === 'offline') {
            presentToast({
                ...defaultToastOptions,
                message: 'Vous êtes hors ligne',
                color: 'danger',
            });
        }
    }, [presentToast, isOnline]);

    const isModePointage = appConfig?.appMode === 'live';
    const isModeManual = appConfig?.appMode === 'manual';

    const isAdherent = mainUser?.type === 'adherent';
    const isGeiq = mainUser?.type === 'geiq';
    const isEmployee = mainUser?.type === 'employee';

    let defaultRedirect = null;

    if (isEmployee) {
        if (isModePointage) {
            defaultRedirect = '/pointage';
        } else if (isModeManual) {
            defaultRedirect = '/saisie';
        }
    } else if (isAdherent) {
        defaultRedirect = '/validation';
    } else if (isGeiq) {
        defaultRedirect = '/schedule';
    }

    return (
        <Switch>
            <Route exact path="/" render={() => <Redirect to="/login" />} />
            <UnLoggedRoute exact path="/login" component={Login} redirecTo={defaultRedirect} />
            <LoggedRoute exact path="/logout" component={Logout} />
            {isModePointage && isEmployee && (
                <LoggedRoute exact path="/pointage/confirmation" component={PointingConfirm} />
            )}
            {isAdherent && (
                <LoggedRoute exact path="/validationdetail/:employeeId/:state" component={ValidationByEmployee} />
            )}
            {isAdherent && <LoggedRoute exact path="/validationconges/:state" component={ValidationLeave} />}
            {isAdherent && <LoggedRoute exact path="/saisiedetail" component={ManualPointing} />}
            {isEmployee && <LoggedRoute exact path="/mesconges/:id" component={DetailLeave} />}

            <IframeRoute path="/iframe/leavecalendar" component={LeaveCalendar} />

            <Route>
                <IonTabs>
                    <IonRouterOutlet id="main">
                        <InsideTabRouter />
                    </IonRouterOutlet>
                    <IonTabBar slot="bottom" className="xl:hidden" id="tab-bar">
                        {isAdherent && (
                            <IonTabButton tab="validationconges" href="/choose-leaves">
                                <IonIcon icon={calendar} />
                                <IonLabel>Validation congés</IonLabel>
                            </IonTabButton>
                        )}
                        {isAdherent && (
                            <IonTabButton tab="validation" href="/validation">
                                <IonIcon icon={checkbox} />
                                <IonLabel>Validation</IonLabel>
                            </IonTabButton>
                        )}
                        {isEmployee && isModePointage && (
                            <IonTabButton tab="pointing" href="/pointage">
                                <IonIcon icon={time} />
                                <IonLabel>Pointage</IonLabel>
                            </IonTabButton>
                        )}
                        {isAdherent && isModePointage && (
                            <IonTabButton tab="manual" href="/saisie">
                                <IonIcon icon={time} />
                                <IonLabel>Saisie</IonLabel>
                            </IonTabButton>
                        )}
                        {(isEmployee || isAdherent) && isModeManual && (
                            <IonTabButton tab="manual" href="/saisie">
                                <IonIcon icon={time} />
                                <IonLabel>Saisie</IonLabel>
                            </IonTabButton>
                        )}

                        {isEmployee && (
                            <IonTabButton tab="mesconges" href="/mesconges">
                                <IonIcon icon={calendar} />
                                <IonLabel>Mes congés</IonLabel>
                            </IonTabButton>
                        )}
                        {/* not in V1 */}
                        {/* {isModePointage && (isEmployee || isAdherent) && (
                            <IonTabButton tab="calendar" href="/calendar">
                                <IonIcon icon={calendar} />
                                <IonLabel>Calendrier</IonLabel>
                            </IonTabButton>
                        )} */}
                        {isEmployee && isModePointage && (
                            <IonTabButton tab="mespointage" href="/mespointage">
                                <IonIcon icon={layers} />
                                <IonLabel>Mes pointages</IonLabel>
                            </IonTabButton>
                        )}
                        {isGeiq && (
                            <IonTabButton tab="schedule" href="/schedule">
                                <IonIcon icon={calendar} />
                                <IonLabel>Planning</IonLabel>
                            </IonTabButton>
                        )}
                        <IonTabButton tab="setting" href="/settings">
                            <IonIcon icon={cog} />
                            <IonLabel>Paramètres</IonLabel>
                        </IonTabButton>
                    </IonTabBar>
                </IonTabs>
            </Route>
        </Switch>
    );
};

// Function for reliable platform detection, designed to avoid incorrect identification when using browser's responsive mode.
export const getImplicitPlatform = () => {
    if (Capacitor.getPlatform() === 'android') {
        return 'android';
    } else if (Capacitor.getPlatform() === 'ios') {
        return 'ios';
    }

    if (isPlatform('ios')) {
        return 'ios';
    }

    if (isPlatform('android')) {
        return 'android';
    }

    return 'web';
};
